/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import {
    ActionTagApi,
    Configuration,
    ConfigurationParameters,
    OfferActionsGetRequest,
} from 'client-code/actions';
import { Constants } from '../constants';
import { TServicesParams } from '../types/TServicesParams';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { CommonHeaders } from '../CommonHeaders';
import { DeviceModel } from '../device/DeviceModel';
import { IActionsServiceRepository } from '../interfaces/IActionsServiceRepository';
import { TActionList, TActionsParams, TCustomActionList } from '../types';

export class ActionsServiceRepositoryImpl implements IActionsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'ActionsServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getOfferActionsFromService(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            const { offerId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: OfferActionsGetRequest = {
                offerId,
                groups,
                channelNumber,
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                acceptLanguage: DeviceModel.getTVProperties().getDateLocale(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const actionsApi = new ActionTagApi(configuration);
            const actionsApiResponseObj = await actionsApi.offerActionsGet(requestParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            error.actionsNetworkCallId = actionsParams.actionsNetworkCallId;
            Log.e(this.TAG, 'getOfferActionsFromService()', error);
            throw error;
        }
    }

    /**
     * function to get offer actions for particular offerId
     * @param actionsParams {TActionsParams}
     * @param actionsNetworkCallId {number}
     * @returns TActionList
     */
    async fetchOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            return await this.getOfferActionsFromService(actionsParams);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
